<template>
    <AuthLayout>
        <div
            class="mt-8 w-full overflow-hidden bg-white p-6 shadow-xl sm:mt-14 sm:max-w-md sm:rounded-lg"
        >
            <div class="mb-8 mt-[2px] border-b border-gray-200 pb-5">
                <h1 class="text-center text-xl font-bold text-gray-800">
                    {{ $t("vue.create_new_account") }}
                </h1>
            </div>

            <form @submit.prevent="submit">
                <div class="flex w-full flex-col items-center gap-2">
                    <SocialiteButton
                        provider="google"
                        label="Google"
                        url="google.auth.index"
                        :invitation-token="invitationToken"
                        class="w-full"
                    />

                    <SocialiteButton
                        provider="microsoft"
                        label="Microsoft"
                        url="microsoft.auth.index"
                        :invitation-token="invitationToken"
                        class="w-full"
                    />
                </div>

                <h5 class="mt-4 pb-4 text-center font-medium text-gray-600">
                    {{ $t("vue.or") }}
                </h5>

                <div class="border-t border-gray-300 pt-4">
                    <TextInput
                        v-model="state.form.email"
                        type="email"
                        autocomplete="email"
                        name="email"
                        required
                        suppress-required-icon
                        autofocus
                        :label="$t('vue.email_address')"
                        :placeholder="$t('vue.enter_your_email_address')"
                        :error="getError('email')"
                        @blur="v$.form.email.$touch()"
                    />
                </div>

                <div class="mt-6">
                    <TextInput
                        v-model="v$.form.name.$model"
                        type="text"
                        autocomplete="name"
                        name="name"
                        required
                        suppress-required-icon
                        :label="$t('vue.your_name')"
                        :placeholder="$t('vue.enter_your_name')"
                        :error="getError('name')"
                    />
                </div>

                <div class="mt-6">
                    <TextInput
                        v-model="state.form.password"
                        type="password"
                        autocomplete="new-password"
                        name="password"
                        required
                        suppress-required-icon
                        :label="$t('vue.set_password')"
                        :placeholder="$t('vue.set_your_password')"
                        :error="getError('password')"
                        @blur="v$.form.password.$touch()"
                    />
                </div>

                <div class="mt-6">
                    <TextInput
                        v-model="state.form.password_confirmation"
                        type="password"
                        autocomplete="new-password"
                        name="password_confirmation"
                        required
                        suppress-required-icon
                        :label="$t('vue.confirm_password')"
                        :placeholder="$t('vue.your_password_again_please')"
                        :error="getError('password_confirmation')"
                        @blur="v$.form.password_confirmation.$touch()"
                    />
                </div>

                <div class="mt-10 flex flex-col items-center pb-3">
                    <MegaButton :disabled="loading" type="submit"
                        >{{ $t("vue.create_account") }}
                    </MegaButton>
                </div>
            </form>
        </div>

        <div class="mt-11 text-center text-sm text-gray-500">
            By registering, you agree to the
            <a
                class="font-medium text-custom-blue-500"
                href="https://dmarceye.com/terms-of-service"
                target="_blank"
                >Terms of Service</a
            >
            and
            <a
                class="font-medium text-custom-blue-500"
                href="https://dmarceye.com/privacy-policy"
                target="_blank"
                >Privacy Policy</a
            >.
        </div>

        <div class="mt-11 text-center text-sm text-gray-500">
            {{ $t("vue.already_have_an_account") }}

            <inertia-link :href="route('login')">
                <span
                    class="font-medium text-custom-blue-500 hover:text-custom-blue-600"
                    >{{ $t("vue.sign_in_exclamation") }}</span
                >
            </inertia-link>
        </div>
    </AuthLayout>

    <Head title="DMARCeye.com registration - DMARC monitoring & reporting">
        <meta
            name="description"
            content="Create your DMARCeye account today and start protecting your domain from spoofing. Set up DMARC monitoring and view reports in minutes."
        />
    </Head>
</template>

<script lang="ts" setup>
import { Head } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useReCaptcha } from "vue-recaptcha-v3";
import { computed, reactive, ref } from "vue";
import {
    required,
    email as emailRule,
    maxLength,
    minLength,
    helpers,
    sameAs,
} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { router } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps({
    email: {
        required: false,
        type: String,
        default: "",
    },
    invitationToken: {
        required: false,
        type: String,
        default: "",
    },
});

// Get access to translations
const { t } = useTranslations();

const reCaptcha = useReCaptcha();

interface IForm {
    form: {
        name: string;
        email: string;
        password: string;
        password_confirmation: string;
        terms: boolean;
        recaptcha_token: string;
        invitation_token: string;
        hubspotutk: string | null;
    };
}

const state: IForm = reactive({
    form: {
        name: "",
        email: props.email,
        password: "",
        password_confirmation: "",
        terms: true,
        recaptcha_token: "",
        invitation_token: props.invitationToken,
        hubspotutk: null,
    },
});

const passwordRef = computed(() => state.form.password);

const rules = {
    form: {
        name: {
            required,
            maxLength: maxLength(255),
        },
        email: {
            required,
            emailRule,
            maxLength: maxLength(255),
        },
        password: {
            required,
            minLength: minLength(8),
            maxLength: maxLength(255),
        },
        password_confirmation: {
            required,
            minLength: minLength(8),
            maxLength: maxLength(255),
            sameAsPassword: helpers.withMessage(
                t("vue.passwords_do_not_match"),
                sameAs(passwordRef),
            ),
        },
    },
};

const v$ = useVuelidate(rules, state);
const errors = ref({});
const loading = ref(false);

const hubspotutk = ref<string | null>(getHubspotUTK());

function getHubspotUTK() {
    const match = document.cookie.match(new RegExp("(^| )hubspotutk=([^;]+)"));
    return match ? match[2] : null;
}

const submit = async () => {
    v$.value.form.$touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        loading.value = true;
        let token = undefined;

        try {
            await reCaptcha?.recaptchaLoaded();
            token = await reCaptcha?.executeRecaptcha("register");

            if (token === undefined) {
                throw new Error();
            }
        } catch (e) {
            loading.value = false;
            errors.value = {
                email: t("vue.recaptcha_validation_failed"),
            };
            return;
        }
        state.form.recaptcha_token = token;
        state.form.hubspotutk = hubspotutk.value ?? undefined;

        router.post(route("register"), state.form, {
            preserveScroll: true,
            onError: (err) => {
                console.log(err);
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
                state.form.password = "";
                state.form.password_confirmation = "";
                v$.value.$reset();
            },
        });
    }
};

const getError = (field: string) => {
    // @ts-ignore
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
